import { useEffect, useRef, useState } from "react";
import { MAIN_HERO_VIDEO_SRC } from "@/components/sections/mainHero/mainHeroVideo.constants";
import { useCms } from "@/lib/cms";
import { resolveMediaUrl } from "@/lib/mediaUrl";

/** Seek slightly before end to avoid visible loop flash on some WebM encodes. */
const LOOP_EPSILON = 0.035;

/**
 * Full-bleed hero background video — hardware-decoded WebM (no Spline/WebGL).
 */
export default function MainHeroVideo({ autoplay = true }) {
  const { cms } = useCms();
  const cmsVideo = resolveMediaUrl(cms?.media_main_hero_video);
  const videoSrc = cmsVideo || MAIN_HERO_VIDEO_SRC;
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const markReady = () => setReady(true);

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      markReady();
    } else {
      video.addEventListener("loadeddata", markReady, { once: true });
    }

    const restartLoop = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      video.currentTime = 0;
      if (autoplay) {
        video.play().catch(() => {});
      }
    };

    const onTimeUpdate = () => {
      const { duration, currentTime } = video;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (duration - currentTime <= LOOP_EPSILON) {
        video.currentTime = 0;
      }
    };

    const onEnded = () => {
      restartLoop();
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);

    if (!autoplay) {
      return () => {
        video.removeEventListener("loadeddata", markReady);
        video.removeEventListener("timeupdate", onTimeUpdate);
        video.removeEventListener("ended", onEnded);
      };
    }

    const tryPlay = () => {
      video.play().catch(() => {});
    };

    tryPlay();
    video.addEventListener("canplay", tryPlay, { once: true });

    const onVisibility = () => {
      if (document.hidden) {
        video.pause();
        return;
      }
      tryPlay();
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      video.removeEventListener("loadeddata", markReady);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
    };
  }, [autoplay]);

  return (
    <div className="ds-main-hero-section__video-layer" aria-hidden="true">
      <div
        className="ds-main-hero-section__video-fallback"
        data-testid="main-hero-video-fallback"
      />
      <video
        key={videoSrc}
        ref={videoRef}
        className={`ds-main-hero-section__video${ready ? " ds-main-hero-section__video--ready" : ""}`}
        autoPlay={autoplay}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        data-testid="main-hero-video"
      >
        <source src={videoSrc} type="video/webm" />
      </video>
    </div>
  );
}
